// src/components/CookieBanner/config.ts
import type { CookieConsentConfig } from 'vanilla-cookieconsent';
import type { ContentString } from '~/shared/types';

/**
 * CookieConsent library:
 *   - API reference: https://cookieconsent.orestbida.com/reference/api-reference.html
 *
 * Category rationale (high level):
 * - Necessary     → Strictly required for security, consent governance, or core access flows.
 *   • Examples: Google reCAPTCHA _GRECAPTCHA (bot mitigation), Google AEC (session security),
 *               Google SOCS (stores cookie/consent state for Google services).
 *   • Legal: typically GDPR Art. 6(1)(f) legitimate interest (security) and/or Art. 6(1)(c) compliance.
 *
 * - Functional    → Improves UX or enables optional site features (not essential to load the site).
 *   • Example: Intercom cookies for chat/messenger.
 *
 * - Analytics     → Measurement/insight. Anonymous by design, still needs consent in EU.
 *   • Examples: GA4 _ga / _ga_* ; HubSpot __hstc/__hssc/__hssrc/hubspotutk.
 *
 * - Marketing     → Ads/retargeting/attribution across sites.
 *   • Examples: Google Ads _gcl_au ; Meta _fbp ; Google prefs NID/__Secure-ENID.
 */

type Args = {
  COOKIE_CONSENT_STORAGE_KEY: string;
  language: 'nl' | 'en';
  cookiePrivacyUrl?: ContentString;
  onFirstConsent: (param: { cookie: CookieConsent.CookieValue }) => void;
  onChange: (param: {
    cookie: CookieConsent.CookieValue;
    changedCategories: Array<string>;
    changedServices: { [key: string]: Array<string> };
  }) => void;
  onConsent: (param: { cookie: CookieConsent.CookieValue }) => void;
};

export const pluginConfig = ({
  COOKIE_CONSENT_STORAGE_KEY,
  language,
  cookiePrivacyUrl,
  onFirstConsent,
  onChange,
  onConsent,
}: Args): CookieConsentConfig => {
  // Render-time origin for first-party cookie table entries
  const FIRST_PARTY =
    typeof window !== 'undefined' ? window.location.origin : '';

  const row = (
    name: string,
    vendor: string,
    domain: string,
    description: string,
    expiration: string,
  ) => ({ name, vendor, domain, description, expiration });

  return {
    onConsent,
    onFirstConsent,
    onChange,

    cookie: {
      name: COOKIE_CONSENT_STORAGE_KEY,
    },

    guiOptions: {
      consentModal: {
        layout: 'cloud',
        position: 'bottom',
        equalWeightButtons: true,
        flipButtons: false,
      },
      preferencesModal: {
        layout: 'box',
        position: 'left',
        equalWeightButtons: false,
        flipButtons: false,
      },
    },

    categories: {
      necessary: { readOnly: true, enabled: true },
      analytics: {},
      marketing: {},
      functional: {},
    },

    language: {
      default: language,
      translations: {
        /* ============================= DUTCH ============================= */
        nl: {
          consentModal: {
            title: 'Deze website maakt gebruik van cookies',
            description:
              'We gebruiken cookies om content en advertenties te personaliseren, om functies voor social media te bieden en om ons websiteverkeer te analyseren.',
            acceptAllBtn: 'Accepteer alle',
            showPreferencesBtn: 'Beheer voorkeuren',
            footer: cookiePrivacyUrl
              ? `<a href='${cookiePrivacyUrl.nl}' target="_blank" rel="noreferrer noopener">Lees meer over cookies</a>`
              : undefined,
          },
          preferencesModal: {
            title: 'Cookievoorkeuren',
            acceptAllBtn: 'Accepteer alle',
            acceptNecessaryBtn: 'Weiger alle',
            savePreferencesBtn: 'Voorkeuren opslaan',
            closeIconLabel: 'Sluiten',
            sections: [
              // Intro
              {
                title: 'Deze website maakt gebruik van cookies',
                description:
                  'We gebruiken cookies om content en advertenties te personaliseren, om functies voor social media te bieden en om ons websiteverkeer te analyseren.',
              },

              /**
               * NECESSARY (Altijd aan)
               *
               * _GRECAPTCHA — reCAPTCHA botbescherming:
               *   Docs (FAQ, gebruik & doel): https://developers.google.com/recaptcha/docs/faq
               *
               * AEC — Google security cookie:
               *   Cookie policy (beschrijving + levensduur ~6 mnd): https://policies.google.com/technologies/cookies?hl=nl
               *
               * SOCS — Google consent state opslag:
               *   Embedded policy (SOCS ~13 mnd): https://policies.google.com/technologies/cookies/embedded?hl=nl
               *
               * Motivering: security en toestemmingsbeheer → noodzakelijk voor betrouwbare dienst.
               */
              {
                title:
                  'Noodzakelijk <span class="pm__badge">Altijd ingeschakeld</span>',
                linkedCategory: 'necessary',
                description:
                  'Noodzakelijke cookies maken basisfuncties, beveiliging en toegangsbeheer mogelijk. Zonder deze cookies kan de website niet naar behoren werken.',
                cookieTable: {
                  headers: {
                    name: 'Naam',
                    domain: 'Domein',
                    vendor: 'Leverancier',
                    description: 'Beschrijving',
                    expiration: 'Vervaldatum',
                  },
                  body: [
                    row(
                      '_GRECAPTCHA',
                      'Google reCAPTCHA',
                      'www.google.com',
                      'Gebruikt voor risicobeoordeling om misbruik door bots te voorkomen (formulieren/acties beschermen).',
                      'Sessie / servicebepaald',
                    ),
                    row(
                      'AEC',
                      'Google',
                      '.google.com',
                      'Beveiligingscookie die verzoeken binnen een sessie aan de gebruiker koppelt en misbruik voorkomt.',
                      '± 6 maanden',
                    ),
                    row(
                      'SOCS',
                      'Google',
                      '.google.com',
                      'Slaat de status van de cookie-/toestemmingskeuzes op voor Google-services.',
                      '± 13 maanden',
                    ),
                  ],
                },
              },

              // COOKIES: Leave it here in case we add these cookies in the future
              // /**
              //  * FUNCTIONAL — Intercom Messenger
              //  *
              //  * Intercom cookie docs (namen/doel/levensduur):
              //  *   https://www.intercom.com/help/en/articles/2361922-intercom-messenger-cookies
              //  *
              //  * Motivering: optionele chat/UX-functionaliteit → functioneel (niet strikt noodzakelijk).
              //  */
              // {
              //   title: 'Functioneel',
              //   linkedCategory: 'functional',
              //   description:
              //     'Functionele cookies verbeteren de prestaties en ervaring (zoals voorkeuren en chat). Ze worden niet gebruikt voor advertentiedoeleinden.',
              //   cookieTable: {
              //     headers: {
              //       name: 'Naam',
              //       domain: 'Domein',
              //       vendor: 'Leverancier',
              //       description: 'Beschrijving',
              //       expiration: 'Vervaldatum',
              //     },
              //     body: [
              //       row(
              //         'intercom-device-id-*',
              //         'Intercom',
              //         FIRST_PARTY,
              //         'Unieke apparaat-ID voor Intercom Messenger ter voorkoming van misbruik.',
              //         'Door Intercom beheerd',
              //       ),
              //       row(
              //         'intercom-id-*',
              //         'Intercom',
              //         FIRST_PARTY,
              //         'Anonieme bezoekers-ID voor Intercom Messenger.',
              //         'Door Intercom beheerd',
              //       ),
              //       row(
              //         'intercom-session-*',
              //         'Intercom',
              //         FIRST_PARTY,
              //         'Sessie-ID per unieke browsersessie t.b.v. Messenger.',
              //         'Door Intercom beheerd',
              //       ),
              //     ],
              //   },
              // },

              /**
               * ANALYTICS — GA4 & HubSpot
               *
               * GA4 cookies:
               *   Support (uitleg _ga / _ga_*): https://support.google.com/analytics/answer/11397207?hl=nl
               *
               * HubSpot cookies:
               *   Lifetimes/roles: https://knowledge.hubspot.com/privacy-and-consent/what-cookies-does-hubspot-set-in-a-visitor-s-browser
               */
              {
                title: 'Analyse',
                linkedCategory: 'analytics',
                description:
                  'Analysecookies helpen website-eigenaren te begrijpen hoe bezoekers de site gebruiken door anoniem gegevens te verzamelen en te rapporteren.',
                cookieTable: {
                  headers: {
                    name: 'Naam',
                    domain: 'Domein',
                    vendor: 'Leverancier',
                    description: 'Beschrijving',
                    expiration: 'Vervaldatum',
                  },
                  body: [
                    row(
                      '_ga',
                      'Google Analytics 4',
                      FIRST_PARTY,
                      'Hoofd-cookie van GA4 om unieke gebruikers te onderscheiden.',
                      '± 2 jaar',
                    ),
                    row(
                      '_ga_*',
                      'Google Analytics 4',
                      FIRST_PARTY,
                      'GA4 cookie met measurement-ID voor sessiestatus / request throttling.',
                      '± 2 jaar',
                    ),
                    row(
                      '__hstc',
                      'HubSpot',
                      FIRST_PARTY,
                      'Hoofd-trackingcookie (domein, user token, eerste/laatste/huidige bezoek, sessieteller).',
                      '± 6 maanden',
                    ),
                    row(
                      '__hssc',
                      'HubSpot',
                      FIRST_PARTY,
                      'Bepaalt of __hstc sessienummer/timestamps moet verhogen.',
                      '± 30 minuten',
                    ),
                    row(
                      '__hssrc',
                      'HubSpot',
                      FIRST_PARTY,
                      'Detecteert of de bezoeker de browser opnieuw heeft gestart (sessie-detector).',
                      'Sessie',
                    ),
                    row(
                      'hubspotutk',
                      'HubSpot',
                      FIRST_PARTY,
                      'Bezoekers-ID; meegestuurd bij formulieren en gebruikt voor deduplicatie.',
                      '± 6 maanden',
                    ),
                  ],
                },
              },

              /**
               * MARKETING — Ads/retargeting
               *
               * Google Ads Conversion Linker (_gcl_au):
               *   https://support.google.com/google-ads/answer/10343003?hl=nl
               *
               * Meta Pixel (_fbp):
               *   https://www.facebook.com/business/help/471978536642445
               *
               * Google prefs/ads cookies (NID / __Secure-ENID):
               *   Category hub: https://business.safety.google/adscookies/
               *   (Algemene policies noemen deze als ads/prefs; exacte levensduren variëren.)
               */
              {
                title: 'Marketing',
                linkedCategory: 'marketing',
                description:
                  'Marketingcookies worden gebruikt om bezoekers over websites te volgen en relevantere advertenties te tonen.',
                cookieTable: {
                  headers: {
                    name: 'Naam',
                    domain: 'Domein',
                    vendor: 'Leverancier',
                    description: 'Beschrijving',
                    expiration: 'Vervaldatum',
                  },
                  body: [
                    row(
                      '_gcl_au',
                      'Google Ads (Conversion Linker)',
                      FIRST_PARTY,
                      'Slaat advertentie-klikinfo op om conversies te meten.',
                      '± 3 maanden',
                    ),
                    row(
                      '_fbp',
                      'Meta (Facebook)',
                      FIRST_PARTY,
                      'Identificeert browsers voor advertentie-levering en retargeting na sitebezoek.',
                      '± 3 maanden',
                    ),
                    row(
                      'NID',
                      'Google',
                      '.google.com',
                      'ID voor voorkeuren en advertentie-/dienstvoorkeuren in Google-services (uitgelogde gebruikers).',
                      'Door Google beheerd (typisch ~6 maanden)',
                    ),
                    row(
                      '__Secure-ENID',
                      'Google',
                      '.google.com',
                      'Beveiligde cookie voor voorkeuren/veiligheidsfuncties; kan advertentiefuncties ondersteunen.',
                      'Door Google beheerd (typisch ~1 jaar)',
                    ),
                  ],
                },
              },

              // Legal/help
              {
                title: 'Meer informatie',
                description: `
                  Niet-essentiële cookies gebruiken wij met uw toestemming (AVG art. 6(1)(a)).
                  <br /><br />
                  U kunt uw toestemming altijd wijzigen via de ronde cookieknop linksonder.
                  <br /><br />
                  Vragen? <a href="mailto:contact@bndl.nl">contact@bndl.nl</a>. Vermeld a.u.b. uw toestemmings-ID en datum.
                `,
              },
            ],
          },
        },

        /* ============================= ENGLISH ============================= */
        en: {
          consentModal: {
            title: 'This website uses cookies',
            description:
              'We use cookies to personalise content and ads, to provide social media features and to analyse our traffic.',
            acceptAllBtn: 'Accept all',
            showPreferencesBtn: 'Manage preferences',
            footer: cookiePrivacyUrl
              ? `<a href='${cookiePrivacyUrl.en}' target="_blank" rel="noreferrer noopener">Read more about cookies</a>`
              : undefined,
          },
          preferencesModal: {
            title: 'Cookie preferences',
            acceptAllBtn: 'Accept all',
            acceptNecessaryBtn: 'Reject all',
            savePreferencesBtn: 'Save preferences',
            closeIconLabel: 'Close',
            sections: [
              // Intro
              {
                title: 'This website uses cookies',
                description:
                  'We use cookies to personalise content and ads, to provide social media features and to analyse our traffic.',
              },

              /**
               * NECESSARY — reCAPTCHA / AEC / SOCS
               * Docs:
               *   reCAPTCHA: https://developers.google.com/recaptcha/docs/faq
               *   AEC:       https://policies.google.com/technologies/cookies?hl=en
               *   SOCS:      https://policies.google.com/technologies/cookies/embedded?hl=en
               */
              {
                title:
                  'Necessary <span class="pm__badge">Always enabled</span>',
                linkedCategory: 'necessary',
                description:
                  'Necessary cookies enable core functionality such as security, network management and accessibility.',
                cookieTable: {
                  headers: {
                    name: 'Name',
                    domain: 'Domain',
                    vendor: 'Vendor',
                    description: 'Description',
                    expiration: 'Expiration',
                  },
                  body: [
                    row(
                      '_GRECAPTCHA',
                      'Google reCAPTCHA',
                      'www.google.com',
                      'Used for risk analysis to distinguish humans from bots and prevent abuse.',
                      'Session / service-defined',
                    ),
                    row(
                      'AEC',
                      'Google',
                      '.google.com',
                      'Security cookie that ties requests within a session to the user and helps prevent abuse.',
                      '≈ 6 months',
                    ),
                    row(
                      'SOCS',
                      'Google',
                      '.google.com',
                      'Stores the user’s cookie/consent choices for Google services.',
                      '≈ 13 months',
                    ),
                  ],
                },
              },

              // COOKIES: Leave it here in case we add these cookies in the future
              // /**
              //  * FUNCTIONAL — Intercom
              //  * Docs: https://www.intercom.com/help/en/articles/2361922-intercom-messenger-cookies
              //  */
              // {
              //   title: 'Functional',
              //   linkedCategory: 'functional',
              //   description:
              //     'Functional cookies enhance performance and experience (e.g., preferences, chat). They are not used for advertising.',
              //   cookieTable: {
              //     headers: {
              //       name: 'Name',
              //       domain: 'Domain',
              //       vendor: 'Vendor',
              //       description: 'Description',
              //       expiration: 'Expiration',
              //     },
              //     body: [
              //       row(
              //         'intercom-device-id-*',
              //         'Intercom',
              //         FIRST_PARTY,
              //         'Identifier per unique device interacting with Intercom Messenger; helps prevent abuse.',
              //         'Managed by Intercom',
              //       ),
              //       row(
              //         'intercom-id-*',
              //         'Intercom',
              //         FIRST_PARTY,
              //         'Anonymous visitor identifier for Intercom Messenger.',
              //         'Managed by Intercom',
              //       ),
              //       row(
              //         'intercom-session-*',
              //         'Intercom',
              //         FIRST_PARTY,
              //         'Identifier for each unique browser session to keep track of sessions.',
              //         'Managed by Intercom',
              //       ),
              //     ],
              //   },
              // },

              /**
               * ANALYTICS — GA4 / HubSpot
               * GA4:     https://support.google.com/analytics/answer/11397207?hl=en
               * HubSpot: https://knowledge.hubspot.com/privacy-and-consent/what-cookies-does-hubspot-set-in-a-visitor-s-browser
               */
              {
                title: 'Analytics',
                linkedCategory: 'analytics',
                description:
                  'Analytics cookies help website owners understand how visitors interact with sites by collecting and reporting information anonymously.',
                cookieTable: {
                  headers: {
                    name: 'Name',
                    domain: 'Domain',
                    vendor: 'Vendor',
                    description: 'Description',
                    expiration: 'Expiration',
                  },
                  body: [
                    row(
                      '_ga',
                      'Google Analytics 4',
                      FIRST_PARTY,
                      'Main GA4 cookie used to distinguish users.',
                      '≈ 2 years',
                    ),
                    row(
                      '_ga_*',
                      'Google Analytics 4',
                      FIRST_PARTY,
                      'GA4 cookie with measurement ID for session state / request throttling.',
                      '≈ 2 years',
                    ),
                    row(
                      '__hstc',
                      'HubSpot',
                      FIRST_PARTY,
                      'Primary visitor tracking cookie (domain, user token, timestamps, session count).',
                      '≈ 6 months',
                    ),
                    row(
                      '__hssc',
                      'HubSpot',
                      FIRST_PARTY,
                      'Determines if HubSpot should increment the session number and timestamps in __hstc.',
                      '≈ 30 minutes',
                    ),
                    row(
                      '__hssrc',
                      'HubSpot',
                      FIRST_PARTY,
                      'Used to determine if the visitor has restarted the browser (session detector).',
                      'Session',
                    ),
                    row(
                      'hubspotutk',
                      'HubSpot',
                      FIRST_PARTY,
                      'Tracks a visitor’s identity; sent to HubSpot on form submission to de-duplicate contacts.',
                      '≈ 6 months',
                    ),
                  ],
                },
              },

              /**
               * MARKETING — Ads/retargeting
               * Google Ads (_gcl_au):     https://support.google.com/google-ads/answer/10343003?hl=en
               * Meta Pixel (_fbp):         https://www.facebook.com/business/help/471978536642445
               * Google Ads cookies hub:    https://business.safety.google/adscookies/
               */
              {
                title: 'Marketing',
                linkedCategory: 'marketing',
                description:
                  'Marketing cookies are used to track visitors across websites to display relevant and engaging ads.',
                cookieTable: {
                  headers: {
                    name: 'Name',
                    domain: 'Domain',
                    vendor: 'Vendor',
                    description: 'Description',
                    expiration: 'Expiration',
                  },
                  body: [
                    row(
                      '_gcl_au',
                      'Google Ads (Conversion Linker)',
                      FIRST_PARTY,
                      'Stores ad click information to help measure conversions.',
                      '≈ 3 months',
                    ),
                    row(
                      '_fbp',
                      'Meta (Facebook)',
                      FIRST_PARTY,
                      'Identifies browsers for ad delivery and retargeting after visiting the site.',
                      '≈ 3 months',
                    ),
                    row(
                      'NID',
                      'Google',
                      '.google.com',
                      'Stores a unique ID to remember preferences and support ad/feature delivery in Google services.',
                      'Typically ~6 months (managed by Google)',
                    ),
                    row(
                      '__Secure-ENID',
                      'Google',
                      '.google.com',
                      'Secure cookie used by Google to remember preferences/other info; can support ad and security functionality.',
                      'Typically ~1 year (managed by Google)',
                    ),
                  ],
                },
              },

              // Legal/help
              {
                title: 'More information',
                description: `
                  We use non-essential cookies with your consent (GDPR Art. 6(1)(a)).
                  <br /><br />
                  You can change or withdraw consent at any time using the round cookie button on this site.
                  <br /><br />
                  Questions? <a href="mailto:contact@bndl.nl">contact@bndl.nl</a>. Please include your consent ID and date.
               `,
              },
            ],
          },
        },
      },
    },
  };
};
